import { Package, CheckCircle, Truck, Store } from "lucide-react";

type OrderStatus = 'pending' | 'confirmed' | 'preparing' | 'ready_for_pickup' | 'shipped' | 'delivered' | 'completed' | 'cancelled';

interface OrderStatusTrackerProps {
  status: OrderStatus;
  deliveryOption: string;
  confirmedAt?: string | null;
  preparingAt?: string | null;
  readyAt?: string | null;
  shippedAt?: string | null;
  deliveredAt?: string | null;
  completedAt?: string | null;
}

export const OrderStatusTracker = ({ 
  status, 
  deliveryOption,
  confirmedAt,
  preparingAt,
  readyAt,
  shippedAt,
  deliveredAt,
  completedAt
}: OrderStatusTrackerProps) => {
  const isPickup = deliveryOption === 'pickup';

  const deliverySteps = [
    { 
      id: 'confirmed', 
      label: 'Confirmée', 
      icon: CheckCircle, 
      timestamp: confirmedAt,
      statuses: ['confirmed', 'preparing', 'shipped', 'delivered', 'ready_for_pickup', 'completed']
    },
    { 
      id: 'preparing', 
      label: 'En préparation', 
      icon: Package, 
      timestamp: preparingAt,
      statuses: ['preparing', 'shipped', 'delivered', 'ready_for_pickup', 'completed']
    },
    { 
      id: 'shipped', 
      label: 'Expédiée', 
      icon: Truck, 
      timestamp: shippedAt,
      statuses: ['shipped', 'delivered']
    },
    { 
      id: 'delivered', 
      label: 'Livrée', 
      icon: CheckCircle, 
      timestamp: deliveredAt,
      statuses: ['delivered']
    },
  ];

  const pickupSteps = [
    { 
      id: 'confirmed', 
      label: 'Confirmée', 
      icon: CheckCircle, 
      timestamp: confirmedAt,
      statuses: ['confirmed', 'preparing', 'ready_for_pickup', 'completed']
    },
    { 
      id: 'preparing', 
      label: 'En préparation', 
      icon: Package, 
      timestamp: preparingAt,
      statuses: ['preparing', 'ready_for_pickup', 'completed']
    },
    { 
      id: 'ready', 
      label: 'Prêt pour retrait', 
      icon: Store, 
      timestamp: readyAt,
      statuses: ['ready_for_pickup', 'completed']
    },
    { 
      id: 'completed', 
      label: 'Récupéré', 
      icon: CheckCircle, 
      timestamp: completedAt,
      statuses: ['completed']
    },
  ];

  const steps = isPickup ? pickupSteps : deliverySteps;

  const getStepStatus = (step: typeof steps[0]) => {
    if (status === 'cancelled') return 'cancelled';
    if (step.statuses.includes(status)) return 'completed';
    if (status === 'pending') return 'pending';
    
    const currentIndex = steps.findIndex(s => s.statuses.includes(status));
    const stepIndex = steps.findIndex(s => s.id === step.id);
    
    return stepIndex < currentIndex ? 'completed' : 'pending';
  };

  const formatDate = (timestamp: string | null | undefined) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (status === 'cancelled') {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-2xl p-6">
        <div className="flex items-center gap-3 text-destructive">
          <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
            <CheckCircle className="w-5 h-5" />
          </div>
          <div>
            <p className="font-semibold">Commande annulée</p>
            <p className="text-sm text-muted-foreground">Cette commande a été annulée</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {steps.map((step, index) => {
        const stepStatus = getStepStatus(step);
        const Icon = step.icon;
        const isActive = stepStatus === 'completed';
        const isPending = stepStatus === 'pending';

        return (
          <div key={step.id} className="relative">
            {/* Connecting Line */}
            {index < steps.length - 1 && (
              <div className="absolute left-5 top-12 w-0.5 h-8 -ml-px">
                <div className={`h-full ${isActive ? 'bg-primary' : 'bg-border'}`} />
              </div>
            )}

            {/* Step */}
            <div className="flex items-start gap-4 pb-2">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                isActive 
                  ? 'bg-primary text-primary-foreground' 
                  : isPending
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 pt-1">
                <p className={`font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step.label}
                </p>
                {step.timestamp && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDate(step.timestamp)}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
